import { forwardRef, useEffect, useRef, useState } from "react";
import { Box, Group, Text } from "@mantine/core";
import * as Cesium from "cesium";
import { BehaviorSelect } from "../force-package-accordions/BehaviorPanel";

interface IDeploymentZoneLayer {
	viewer: Cesium.Viewer;
	defaultValue: number | null;
	previewMode?: boolean;
	onChange: (value: string | null) => void;
}

const DeploymentZoneLayer: React.FC<IDeploymentZoneLayer> = ({ viewer, defaultValue, previewMode, onChange }) => {
	const [areasList, setAreasList] = useState<{ value: string; label: string }[]>();
	const [value, setValue] = useState<string | null>(defaultValue?.toString() || null);
	const valueRef = useRef<string | null>(defaultValue?.toString() || null);

	const toggleArea = (areaId: number, toggle: boolean) => {
		if (viewer && viewer.dataSources) {
			const areasDataSource = viewer.dataSources.getByName("areas")[0] as Cesium.GeoJsonDataSource | undefined;
			const areasEntities = areasDataSource?.entities.values;
			const side = areasDataSource?.entities?.values[0]?.properties?.type.getValue(new Cesium.JulianDate(1, 1)).includes("Blue")
				? 0
				: 1;

			if (areasEntities) {
				areasEntities
					.filter((entity) => entity?.properties?.id.getValue(new Cesium.JulianDate(1, 1)) === areaId)
					.forEach((entity) => {
						const originalColor = side ? Cesium.Color.RED.withAlpha(0.4) : Cesium.Color.BLUE.withAlpha(0.4);
						if (entity.polygon && entity?.polygon?.material) {
							entity.polygon.outlineColor = new Cesium.ConstantProperty(originalColor);
							entity.polygon.material = toggle
								? new Cesium.ColorMaterialProperty(Cesium.Color.GOLDENROD.withAlpha(1))
								: new Cesium.ColorMaterialProperty(originalColor);
						}
					});
			}
		}
	};

	useEffect(() => {
		const areasDataSource = viewer.dataSources.getByName("areas")[0] as Cesium.GeoJsonDataSource | undefined;
		const areasEntities = areasDataSource?.entities.values;

		if (areasEntities) {
			setAreasList(
				areasEntities
					.filter((area) => area?.properties?.type.getValue(new Cesium.JulianDate(1, 1)).includes("staging"))
					.map((area) => {
						const id = area?.properties?.id.getValue(new Cesium.JulianDate(1, 1));
						const type = area?.properties?.type.getValue(new Cesium.JulianDate(1, 1));
						return {
							value: id.toString(),
							label: type + " " + id,
						};
					})
			);
		}

		return () => {
			valueRef.current !== null && toggleArea(parseInt(valueRef.current), false);
		};

		// We only need to run it at the beginning
	}, []);

	useEffect(() => {
		if (value !== null) {
			toggleArea(parseInt(value), true);
		}
	}, [value]);

	interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
		image: string;
		label: string;
		description: string;
	}

	const SelectItem = forwardRef<HTMLDivElement, ItemProps>(({ label, ...others }: ItemProps, ref) => (
		<div
			ref={ref}
			{...others}
			onMouseEnter={(e) => {
				if (others.onMouseEnter) {
					others.onMouseEnter(e);
				}

				const itemValue = e.currentTarget.getAttribute("value");
				if (value !== itemValue && itemValue) {
					toggleArea(parseInt(itemValue), true);
				}
			}}
			onMouseLeave={(e) => {
				if (others.onMouseLeave) {
					others.onMouseLeave(e);
				}

				const itemValue = e.currentTarget.getAttribute("value");
				if (value !== itemValue && itemValue) {
					toggleArea(parseInt(itemValue), false);
				}
			}}
		>
			<Group noWrap>
				<Box>
					<Text size="sm">{label}</Text>
				</Box>
			</Group>
		</div>
	));

	return !previewMode && areasList ? (
		<BehaviorSelect
			defaultValue={defaultValue?.toString()}
			onChange={(newValue) => {
				if (value !== null) toggleArea(parseInt(value), false);
				valueRef.current = newValue;
				setValue(newValue);
				onChange(newValue);
			}}
			value={value}
			data={areasList}
			clearable
			itemComponent={SelectItem}
		></BehaviorSelect>
	) : null;
};

export default DeploymentZoneLayer;
